import { useContext, useEffect, useState } from "react";
import { SettingsContext, ShopContext } from "../App";
import currencyFormat, { textLabel } from "./functions/functions";
import QtySelector from "./QtySelector";
import CloseButton from "./CloseButton";
import Label from "./Label";
import SubproductInfo, { SubproductDetails } from "./SubproductInfo";
import "../css/Variants.css";

function Variants({ product }) {
	const { settings, setSelectedProduct, handleAddProduct, labels, lang } =
		useContext(SettingsContext);
	const { shop } = useContext(ShopContext);
	const [infoProduct, setInfoProduct] = useState(null);
	const [buttonEnabled, serButtonEnabled] = useState(false);
	const [finalPrice, setFinalPrice] = useState(product.price);
	const [errorEl, setErrorEl] = useState(false);
	// scelte fatte per gruppo (indice del gruppo → quantità), aggiornate da checkVariants()
	const [counts, setCounts] = useState({});
	// l'errore sui gruppi si mostra solo dopo il primo tocco su "Aggiungi"
	const [submitted, setSubmitted] = useState(false);
	const [pulseIndex, setPulseIndex] = useState(null);

	useEffect(() => {
		checkVariants();
	}, []);

	// testo di un'etichetta con segnaposto, es. t(195, { min: 1, max: 2 })
	const t = (number, vars = {}) => {
		let text = textLabel(labels, number, lang) || "";
		Object.keys(vars).forEach((key) => {
			text = text.split("{" + key + "}").join(vars[key]);
		});
		return text;
	};

	const checkVariants = () => {
		let valid = true;
		let price = product.discounted
			? parseFloat(product.discount.price)
			: parseFloat(product.price);
		let firts_element_error = false;
		const newCounts = {};
		const variants_categories = document.querySelectorAll(".variants-category");
		variants_categories.forEach((variants_category) => {
			const variants = variants_category.querySelectorAll(".variant");
			let selected = 0;
			const categoryHasPrice = parseInt(variants_category.dataset.hasPrice, 10) === 1;
			variants.forEach((variant) => {
				variant.querySelectorAll("button").forEach((input) => {
					if (input.classList.contains("checked")) {
						selected++;
						if (categoryHasPrice) price += parseFloat(variant.dataset.price || 0);
					}
				});
				variant.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
					selected++;
					if (categoryHasPrice) price += parseFloat(variant.dataset.price || 0);
				});
				variant.querySelectorAll(".qty").forEach((input) => {
					selected += parseInt(input.dataset.qty);
					if (categoryHasPrice) {
						price += parseInt(input.dataset.qty) * parseFloat(variant.dataset.price || 0);
					}
				});
			});
			newCounts[variants_category.dataset.index] = selected;
			if (selected < parseInt(variants_category.dataset.min)) {
				valid = false;
				if (!firts_element_error) firts_element_error = variants_category;
			}
			variants.forEach((variant) => {
				variant.querySelectorAll('input[type="checkbox"]').forEach((input) => {
					if (selected >= variants_category.dataset.max) {
						if (!input.checked) input.disabled = true;
					} else {
						input.disabled = false;
					}
				});
				variant.querySelectorAll(".qty-selector").forEach((qty_box) => {
					const plusButton = qty_box.querySelector("button.plus");
					if (selected >= variants_category.dataset.max) {
						plusButton.classList.remove("enabled");
					} else {
						plusButton.classList.add("enabled");
					}
				});
			});
		});
		serButtonEnabled(valid);
		setFinalPrice(price);
		setErrorEl(firts_element_error);
		setCounts(newCounts);
	};

	// "Aggiungi" con scelte mancanti: mostra l'errore e porta al primo gruppo incompleto
	const showMissing = () => {
		setSubmitted(true);
		if (!errorEl) return;
		setPulseIndex(errorEl.dataset.index);
		setTimeout(() => setPulseIndex(null), 900);
		errorEl.scrollIntoView({ behavior: "smooth", block: "start" });
	};

	const handleAddToCart = () => {
		const variants_categories = document.querySelectorAll(".variants-category");
		let selectedVariants = [];
		let selectedSubproducts = [];
		variants_categories.forEach((variants_category) => {
			const variants = variants_category.querySelectorAll(".variant");
			variants.forEach((variant) => {
				variant.querySelectorAll("button.checked").forEach((input) => {
					if (variant.classList.contains("subproduct")) {
						selectedSubproducts.push({
							variant_category_id: variants_category.dataset.id,
							id: variant.dataset.id,
							qty: 1,
						});
					} else {
						selectedVariants.push({
							variant_category_id: variants_category.dataset.id,
							id: variant.dataset.id,
							qty: 1,
						});
					}
				});
				variant.querySelectorAll('input[type="checkbox"]:checked').forEach((input) => {
					if (variant.classList.contains("subproduct")) {
						selectedSubproducts.push({
							variant_category_id: variants_category.dataset.id,
							id: variant.dataset.id,
							qty: 1,
						});
					} else {
						selectedVariants.push({
							variant_category_id: variants_category.dataset.id,
							id: variant.dataset.id,
							qty: 1,
						});
					}
				});
				variant.querySelectorAll(".qty").forEach((input) => {
					if (input.dataset.qty > 0) {
						if (variant.classList.contains("subproduct")) {
							selectedSubproducts.push({
								variant_category_id: variants_category.dataset.id,
								id: variant.dataset.id,
								qty: input.dataset.qty,
							});
						} else {
							selectedVariants.push({
								variant_category_id: variants_category.dataset.id,
								id: variant.dataset.id,
								qty: input.dataset.qty,
							});
						}
					}
				});
			});
		});
		let notes = "";
		const ingredients = document.querySelectorAll(".ingredient input[type='checkbox']");
		ingredients.forEach((ingredient) => {
			if (ingredient.checked) notes += "No " + ingredient.dataset.name + "<br>";
		});
		handleAddProduct({
			...product,
			qty: 1,
			variants: selectedVariants,
			products: selectedSubproducts,
			notes: notes,
		});
	};

	const ruleText = (min, max) => {
		if (min === 0) return t(196, { max });
		if (min === max) return t(198, { n: min });
		return t(195, { min, max });
	};

	const renderHead = (variants_category, index) => {
		const min = parseInt(variants_category.min) || 0;
		const max = parseInt(variants_category.max) || 0;
		const count = counts[index] || 0;
		const hasError = submitted && count < min;
		let chip;
		if (hasError) chip = { cls: "err", text: t(197, { n: min - count }) };
		else if (min > 0 && count >= min)
			chip = { cls: "ok", text: max > 1 ? count + "/" + max + " ✓" : "✓" };
		else if (min > 0) chip = { cls: "req", text: t(193) };
		else chip = { cls: "opt", text: count > 0 ? count + "/" + max : t(194) };
		return (
			<div className="variants-category-head">
				<div>
					<div className="variants-category-title">{variants_category.name}</div>
					{hasError ? (
						<div className="variants-category-error">{t(199)}</div>
					) : (
						<div className="variants-category-rule">{ruleText(min, max)}</div>
					)}
				</div>
				<span className={"variants-category-chip " + chip.cls}>{chip.text}</span>
			</div>
		);
	};

	return (
		<div id="variants" className="popup">
			<div className="content">
				<div className="popup-header">
					<CloseButton onClick={() => setSelectedProduct(false)} />
					<h3 className="mb-0">{product.name}</h3>
					{currencyFormat(finalPrice, false, shop.currency)}
				</div>
				<div className="popup-scroll">
					<div className="popup-content">
						{(product.variants_categories.length > 0 ||
							product.ingredients.length > 0) && (
							<>
								<div
									className="subtitle"
									style={{
										backgroundColor: settings.selfordering.color1,
										color: settings.selfordering.color2,
									}}
								>
									<Label number={43} />
								</div>

								<div className="variants-categories">
									{product.variants_categories.map((variants_category, index) => {
										const hasError =
											submitted &&
											(counts[index] || 0) <
												(parseInt(variants_category.min) || 0);
										const hasImg = variants_category.products.some((p) => p.img);
										return (
											<div
												key={variants_category.id}
												className={
													"variants-category" +
													(hasError ? " has-error" : "") +
													(hasError && String(pulseIndex) === String(index)
														? " pulse"
														: "")
												}
												data-id={variants_category.id}
												data-index={index}
												data-min={variants_category.min}
												data-max={variants_category.max}
												data-has-price={variants_category.has_price}
											>
												{renderHead(variants_category, index)}
												{variants_category.max > 1 &&
												variants_category.force_mono == 0 ? (
													<div className="variants multi">
														{[
															...variants_category.variants,
															...variants_category.variants2,
														].map((variant, i) => (
															<VariantRow
																key={"v-" + i}
																item={variant}
																hasPrice={variants_category.has_price}
																hasImg={hasImg}
																control={
																	<QtySelector
																		variant={variant}
																		onChange={checkVariants}
																	/>
																}
															/>
														))}
														{variants_category.products.map((subproduct, i) => (
															<VariantRow
																key={"s-" + i}
																item={subproduct}
																subproduct
																hasPrice={variants_category.has_price}
																hasImg={hasImg}
																onInfo={setInfoProduct}
																control={
																	<QtySelector
																		variant={subproduct}
																		onChange={checkVariants}
																	/>
																}
															/>
														))}
													</div>
												) : variants_category.force_mono == 1 ? (
													<Checkbox
														variants_category={variants_category}
														onchange={checkVariants}
														onInfo={setInfoProduct}
														hasImg={hasImg}
													/>
												) : (
													<Radio
														variants_category={variants_category}
														onchange={checkVariants}
														onInfo={setInfoProduct}
														hasImg={hasImg}
													/>
												)}
											</div>
										);
									})}
									{product.ingredients.length > 0 &&
										product.ingredients.filter((i) => i.removable == 1).length >
											0 && (
											<div className="ingredients-container">
												<div className="variants-category-title">
													<Label number={42} />
												</div>
												<div className="small mb-1">
													<Label number={99} />
												</div>
												<div className="ingredients">
													{product.ingredients.map(
														(ingredient) =>
															ingredient.removable == 1 && (
																<div
																	className="ingredient"
																	key={"ing-" + ingredient.id}
																>
																	<input
																		type="checkbox"
																		value={ingredient.name}
																		data-name={ingredient.name}
																		disabled={
																			ingredient.removable ==
																			0
																		}
																		id={"ing-" + ingredient.id}
																	/>
																	<span className="checkbox_checkmark"></span>
																	<label
																		htmlFor={
																			"ing-" + ingredient.id
																		}
																		className="variant-title"
																	>
																		<Label number={118} />{" "}
																		{ingredient.name}
																	</label>
																</div>
															)
													)}
												</div>
											</div>
										)}
								</div>
							</>
						)}
					</div>
				</div>
				<div
					className="finalPrice"
					style={{
						backgroundColor: settings.selfordering.color3,
						color: settings.selfordering.color4,
					}}
				>
					{currencyFormat(finalPrice, true, shop.currency)}
				</div>
				<div className="buttons">
					<button className="button" onClick={() => setSelectedProduct(false)}>
						<Label number={6} />
					</button>
					<button
						className="button"
						onClick={
							product.disabled != 1 && buttonEnabled ? handleAddToCart : showMissing
						}
						style={{
							backgroundColor: settings.selfordering.color1,
							color: settings.selfordering.color2,
						}}
					>
						<Label number={15} />
					</button>
				</div>
			</div>
			{infoProduct && (
				<SubproductInfo product={infoProduct} onClose={() => setInfoProduct(null)} />
			)}
		</div>
	);
}

// Riga di una variante: testo a sinistra, foto 48px a destra con il controllo sull'angolo.
// Senza foto il controllo sta a destra; se il gruppo ha altre foto, al posto della foto c'è un segnaposto.
function VariantRow({ item, subproduct, hasPrice, hasImg, onInfo, control, labelFor }) {
	const { shop } = useContext(ShopContext);
	const price = parseFloat(item.price) || 0;
	const title = labelFor ? (
		<label htmlFor={labelFor} className="variant-title">
			{item.name}
		</label>
	) : (
		<span className="variant-title">{item.name}</span>
	);
	const showPhoto = hasImg || item.img;

	return (
		<div
			className={
				"variant v-row" + (subproduct ? " subproduct" : "") + (showPhoto ? " has-photo" : "")
			}
			data-id={item.id}
			data-price={item.price ? item.price : 0}
		>
			<div className="v-text">
				{subproduct ? (
					<SubproductDetails product={item} onInfo={onInfo}>
						{title}
					</SubproductDetails>
				) : (
					title
				)}
				{hasPrice == 1 && price > 0 && (
					<div className="v-price">{"+ " + currencyFormat(price, false, shop.currency)}</div>
				)}
			</div>
			{showPhoto ? (
				<div className="v-photo">
					{item.img ? (
						<img src={item.img} alt="" />
					) : (
						<span className="v-photo-ph">{(item.name || "").charAt(0)}</span>
					)}
					{control}
				</div>
			) : (
				<div className="v-side">{control}</div>
			)}
		</div>
	);
}

function Checkbox({ variants_category, onchange, onInfo, hasImg }) {
	const box = (id, defaultChecked) => (
		<span className="v-ctrl">
			<input type="checkbox" id={id} onChange={onchange} defaultChecked={defaultChecked} />
			<span className="checkbox_checkmark"></span>
		</span>
	);
	return (
		<div className="variants">
			<div className="variants multi">
				{variants_category.variants.map((variant, i) => {
					const id = "var-" + variants_category.id + "-" + variant.id;
					return (
						<VariantRow
							key={"v-" + i}
							item={variant}
							hasPrice={variants_category.has_price}
							hasImg={hasImg}
							labelFor={id}
							control={box(id, false)}
						/>
					);
				})}
				{variants_category.variants2.map((variant, i) => {
					const id = "var-" + variants_category.id + "-" + variant.id;
					return (
						<VariantRow
							key={"v2-" + i}
							item={variant}
							hasPrice={variants_category.has_price}
							hasImg={hasImg}
							labelFor={id}
							control={box(id, parseInt(variant.is_default) > 0)}
						/>
					);
				})}
				{variants_category.products.map((subproduct, i) => {
					const id = "var-" + variants_category.id + "-" + subproduct.id;
					return (
						<VariantRow
							key={"s-" + i}
							item={subproduct}
							subproduct
							hasPrice={variants_category.has_price}
							hasImg={hasImg}
							onInfo={onInfo}
							labelFor={id}
							control={box(id, variants_category.products.length == 1)}
						/>
					);
				})}
			</div>
		</div>
	);
}

function Radio({ variants_category, onchange, onInfo, hasImg }) {
	const handleClick = (event) => {
		const el = event.currentTarget;
		const wasChecked = el.classList.contains("checked");
		document
			.querySelectorAll("[name=" + el.name + "]")
			.forEach((e) => e.classList.remove("checked"));
		if (!wasChecked) el.classList.add("checked");
		onchange();
	};

	const radio = (id) => (
		<span className="v-ctrl">
			<button
				type="button"
				id={id}
				name={"var-" + variants_category.id}
				className="radio"
				onClick={handleClick}
			/>
		</span>
	);

	return (
		<div className="variants">
			<div className="variants multi">
				{[...variants_category.variants, ...variants_category.variants2].map((variant, i) => {
					const id = "var-" + variants_category.id + "-" + variant.id;
					return (
						<VariantRow
							key={"v-" + i}
							item={variant}
							hasPrice={variants_category.has_price}
							hasImg={hasImg}
							labelFor={id}
							control={radio(id)}
						/>
					);
				})}
				{variants_category.products.map((subproduct, i) => {
					const id = "var-" + variants_category.id + "-" + subproduct.id;
					return (
						<VariantRow
							key={"s-" + i}
							item={subproduct}
							subproduct
							hasPrice={variants_category.has_price}
							hasImg={hasImg}
							onInfo={onInfo}
							labelFor={id}
							control={radio(id)}
						/>
					);
				})}
			</div>
		</div>
	);
}

export default Variants;
